import { StandaloneMapProps } from '@/components/maps/StandaloneMap';
import { isSidecarEvent, SidecarMessage } from '@/lib/sidecar';
import { createContext, ReactElement, RefObject, useEffect, useRef } from 'react';

class MultiWindowManager {
  #mapWindow: Window | null = null;

  constructor() {}

  openMap(opts: StandaloneMapProps, launch = false) {
    if ((!this.#mapWindow || this.#mapWindow.closed) && launch) {
      this.#mapWindow = window.open(
        `${window.origin}/sidecar`,
        'MapWindow',
        'width=1024,height=768,menubar=no,toolbar=no,location=no,status=no'
      );

      if (!this.#mapWindow) {
        return;
      }

      const readyListener = (event: MessageEvent) => {
        if (!event.origin.startsWith(window.location.origin)) {
          console.log('Invalid origin ' + event.origin);
          return;
        }

        const { data } = event;
        if (!isSidecarEvent(data)) {
          return;
        }

        if (data.sidecarMessage === 'loaded') {
          this.#mapWindow?.postMessage(
            { sidecarMessage: 'openMap', ...opts } as SidecarMessage,
            window.location.href
          );
        }

        window.removeEventListener('message', readyListener);
      };

      window.addEventListener('message', readyListener);

      this.#mapWindow.addEventListener('beforeunload', () => {
        this.#mapWindow = null;
        window.removeEventListener('message', readyListener);
      });
    }

    this.#mapWindow?.postMessage(
      { sidecarMessage: 'openMap', ...opts } as SidecarMessage,
      window.location.href
    );
  }

  clearMap() {
    this.#mapWindow?.postMessage(
      { sidecarMessage: 'noAlert' } as SidecarMessage,
      window.location.href
    );
  }

  destroy() {
    if (this.#mapWindow) {
      this.#mapWindow.close();
    }
  }
}

export const MultiWindowContext = createContext<undefined | RefObject<MultiWindowManager>>(
  undefined
);

type MultiWindowProviderProps = { children: ReactElement };
export const MultiWindowProvider = ({ children }: MultiWindowProviderProps) => {
  const multiWindowRef = useRef(new MultiWindowManager());

  useEffect(() => {
    const multiWindow = multiWindowRef.current;

    return () => {
      multiWindow.destroy();
    };
  }, []);

  return (
    <MultiWindowContext.Provider value={multiWindowRef}>{children}</MultiWindowContext.Provider>
  );
};
